/**
 * Backoff schedule for re-establishing the notification subscription.
 *
 * Notifications are a single AppSync subscription with no polling fallback, so
 * while it is down the app silently stops telling anyone their match is ready —
 * the one thing it most needs to do. At a venue the socket goes down routinely:
 * a phone locks, the tab backgrounds, wifi hands over to cellular.
 *
 * Jitter is not decoration here. A tournament is a room full of clients on one
 * access point, so a wifi blip drops them all at the same instant and an
 * unjittered schedule would have every phone retry in the same millisecond,
 * repeatedly. Spreading each client's delay over the back half of its window
 * keeps the reconnect storm smooth without materially slowing any one client.
 */

/** First retry lands ~0.5–1s after the drop: fast enough to be invisible. */
export const BASE_RECONNECT_DELAY_MS = 1_000;

/**
 * Ceiling on the wait. 30s is chosen against the failure it has to cover: a
 * player glancing at their phone between battles should never be more than
 * half a minute of staleness away, and `visibilitychange` usually beats the
 * timer to it anyway.
 */
export const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * How long to wait before retry number `attempt` (0-based).
 *
 * `random` is injectable so the schedule can be asserted exactly in tests
 * rather than approximately.
 */
export function reconnectDelay(attempt: number, random: () => number = Math.random): number {
  const uncapped = BASE_RECONNECT_DELAY_MS * 2 ** Math.max(0, attempt);
  const ceiling = Math.min(uncapped, MAX_RECONNECT_DELAY_MS);

  // Half-to-full jitter: never retry instantly (which would hammer a server
  // that is already unhappy), but never wait the full window either.
  return Math.round(ceiling * (0.5 + 0.5 * random()));
}

/** Tears down one live subscription. */
export type Unsubscribe = () => void;

export interface ReconnectingSubscriptionOptions<T> {
  /** Open a subscription. Async because the transport module is lazily imported. */
  subscribe: (
    onMessage: (message: T) => void,
    onError: (error: Error) => void
  ) => Promise<Unsubscribe>;
  /** Called for each delivered message while connected. */
  onMessage: (message: T) => void;
  /**
   * Called after every *re*connect, never after the first connect. Resubscribing
   * resumes the live feed but says nothing about what arrived while the socket
   * was down, and that gap is the whole problem — the match that became ready
   * while the phone was locked.
   */
  onReconnected: () => void | Promise<void>;
  /** Injectable for tests. */
  delay?: (attempt: number) => number;
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (handle: ReturnType<typeof setTimeout>) => void;
}

export interface ReconnectingSubscription {
  /** Open the first connection. */
  start: () => Promise<void>;
  /**
   * Reconnect right now, skipping any pending backoff. For events that mean
   * "the socket is probably dead but will never say so" — returning to the
   * foreground, or the network coming back. On iOS a backgrounded socket dies
   * silently, so no error is ever raised and backoff alone would never fire.
   */
  reconnectNow: () => Promise<void>;
  /** Permanently stop: cancels pending retries and drops the connection. */
  stop: () => void;
  /** Consecutive failures since the last delivered message. Exposed for tests. */
  readonly attempt: number;
}

/**
 * A subscription that re-establishes itself after failure.
 *
 * Extracted from the React effect that drives it because the interesting
 * behaviour is all lifecycle — that a burst of errors schedules one retry and
 * not five, that reconnecting closes the old socket before opening a new one,
 * and that stopping really stops — none of which is observable by looking at
 * the component.
 */
export function createReconnectingSubscription<T>(
  options: ReconnectingSubscriptionOptions<T>
): ReconnectingSubscription {
  const {
    subscribe,
    onMessage,
    onReconnected,
    delay = reconnectDelay,
    setTimer = setTimeout,
    clearTimer = clearTimeout,
  } = options;

  let unsubscribe: Unsubscribe | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  let attempt = 0;

  const drop = () => {
    unsubscribe?.();
    unsubscribe = undefined;
  };

  const connect = async () => {
    if (stopped) return;

    const handle = await subscribe(
      (message) => {
        // A delivered message proves the socket is healthy, so the next failure
        // starts from a short delay instead of wherever backoff had climbed to.
        attempt = 0;
        onMessage(message);
      },
      scheduleReconnect
    );

    // stop() may have been called while the transport module was loading.
    if (stopped) {
      handle();
      return;
    }
    unsubscribe = handle;
  };

  function scheduleReconnect(): void {
    // A failing subscription can report several errors in a row. Without this
    // guard each one books its own timer and the retries multiply.
    if (stopped || retryTimer) return;

    drop();
    retryTimer = setTimer(() => {
      retryTimer = undefined;
      attempt += 1;
      void reconnectNow();
    }, delay(attempt));
  }

  async function reconnectNow(): Promise<void> {
    if (stopped) return;
    if (retryTimer) {
      clearTimer(retryTimer);
      retryTimer = undefined;
    }
    drop();
    await connect();
    if (!stopped) await onReconnected();
  }

  return {
    start: connect,
    reconnectNow,
    stop: () => {
      stopped = true;
      if (retryTimer) clearTimer(retryTimer);
      retryTimer = undefined;
      drop();
    },
    get attempt() {
      return attempt;
    },
  };
}
